/**
 * Registre des générateurs d'ITEMS NATIONAUX (évaluations nationales de maths et de français, campagne 2026).
 *
 * Un générateur par item fabrique des variantes de l'item pour le QCM de suivi (bilan) : même type,
 * même difficulté, mêmes erreurs. Rangés à part des exerciseurs du référentiel : le registre des attendus
 * (scripts/gen-registry.js) ne scanne que src/generators/<matière>/, et ces générateurs n'ont pas
 * d'attendu. Ils passent le même harnais (evalnat.qa.test.js).
 *
 * Un module par niveau et par test (2026/6e/automatismes.js…) : squelette d'un gen (CATEGORIES,
 * generate) + ITEMS = [{ questionNum, formatKey, libelle, bonneReponseOfficielle }].
 *
 * GARDE-FOU : une variante n'est proposée que pour un item OFFICIEL dont la bonne réponse en base est
 * celle que le générateur reproduit. Si le ministère change l'item une autre année, son générateur ne
 * s'applique plus, plutôt que de fabriquer des variantes d'un item qui n'existe plus.
 */
import { normalizeAnswer } from '../../utils/questionUtils';
import { formatDeSaisie, VERS_FORMAT } from './formats';
import * as automatismes6e from './2026/6e/automatismes';
import * as problemes6e from './2026/6e/problemes';
import * as automatismes5e from './2026/5e/automatismes';
import * as automatismes4e from './2026/4e/automatismes';
import * as problemes4e from './2026/4e/problemes';
// Français (10/10/2026) : un module par test ; le type_label de la base nomme le support de lecture
import * as lexique6e from './2026/francais/6e/lexique';
import * as arachne6e from './2026/francais/6e/arachne';
import * as sommeil6e from './2026/francais/6e/sommeil';
import * as competences5e from './2026/francais/5e/competences';
import * as lexique4e from './2026/francais/4e/lexique';
import * as petitChose4e from './2026/francais/4e/petit_chose';
import * as volcans4e from './2026/francais/4e/volcans';

export const CAMPAGNE = '2026';

/** Les modules, avec le niveau (classes_concernees) et le type_label de leurs items en base. */
export const MODULES_EVALNAT = [
    { niveau: '6', typeLabel: 'Automatisme', module: automatismes6e },
    { niveau: '6', typeLabel: 'Résolution de problèmes', module: problemes6e },
    { niveau: '5', typeLabel: 'Automatisme', module: automatismes5e },
    { niveau: '4', typeLabel: 'Automatisme', module: automatismes4e },
    { niveau: '4', typeLabel: 'Résolution de problèmes', module: problemes4e },
    { niveau: '6', typeLabel: 'Lexique', module: lexique6e },
    { niveau: '6', typeLabel: "Compréhension de l'écrit Arachné", module: arachne6e },
    { niveau: '6', typeLabel: "Compréhension de l'écrit Document Sommeil", module: sommeil6e },
    { niveau: '5', typeLabel: 'Compétences langagières et linguistiques', module: competences5e },
    { niveau: '4', typeLabel: 'Lexique', module: lexique4e },
    { niveau: '4', typeLabel: "Compréhension de l'écrit Petit Chose", module: petitChose4e },
    { niveau: '4', typeLabel: "Compréhension de l'écrit Volcans sous la glace", module: volcans4e },
];

const slug = (s) => normalizeAnswer(String(s)).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/** La clé d'une variante générée (champ generator_key de la question) : campagne, niveau, test, item. */
export const generatorKeyItem = (niveau, typeLabel, questionNum) =>
    `evalnat-${CAMPAGNE}:${niveau}:${slug(typeLabel)}:${questionNum}`;

/**
 * Le type d'un tableau à cocher, par la règle du quiz pour un item officiel relu depuis sa bonne réponse
 * (Quiz.jsx, buildTableConfig) : Vrai/Faux ; sinon « association » quand chaque colonne ne sert qu'une fois ;
 * sinon colonnes libres. Une variante en tableau s'affiche ainsi comme l'item de septembre.
 */
export const typeDeTableau = ({ colonnes = [], bonnesReponses = [] }) => {
    const vraiFaux = colonnes.length === 2 && colonnes.every((c) => ['vrai', 'faux'].includes(String(c).toLowerCase()));
    if (vraiFaux) return 'vraiFaux';
    return new Set(bonnesReponses).size === bonnesReponses.length ? 'association' : 'colonnesLibres';
};

const tableauCommeLOfficiel = (payload) => {
    const elements = payload?.elements_additionnels?.elements;
    if (!Array.isArray(elements) || !elements.some((e) => e?.type === 'table')) return payload;
    return {
        ...payload,
        elements_additionnels: {
            ...payload.elements_additionnels,
            elements: elements.map((e) => (e?.type === 'table'
                ? { ...e, config: { ...e.config, type: typeDeTableau(e.config || {}) } }
                : e)),
        },
    };
};

/** Le registre de modules donnés (exporté pour les tests). */
export const creerRegistre = (modules) => {
    const parItem = new Map();
    modules.forEach(({ niveau, typeLabel, module }) => {
        (module.ITEMS || []).forEach((item) => parItem.set(`${niveau}|${typeLabel}|${item.questionNum}`, { niveau, typeLabel, module, item }));
    });

    /** Le générateur d'une question OFFICIELLE de la classe de ce niveau, ou null. */
    const generateurDe = (question, niveau) => {
        if (!question?.est_nationale) return null;
        const g = parItem.get(`${niveau}|${question.type_label}|${question.question_num}`);
        if (!g) return null;
        const officielle = normalizeAnswer(String(question.bonne_reponse ?? ''));
        return officielle === normalizeAnswer(g.item.bonneReponseOfficielle) ? g : null;
    };

    return {
        aUnGenerateur: (question, niveau) => generateurDe(question, niveau) !== null,
        /** Le format à saisir de l'item, 'saisie' ou 'trous' (formats.js) ; null : il reste en QCM. */
        formatASaisir: (question, niveau) => {
            const g = generateurDe(question, niveau);
            return g ? formatDeSaisie(g.niveau, g.typeLabel, g.item.questionNum) : null;
        },
        /** Une seule variante pour l'item (compréhension de l'écrit : un nouveau texte, une question par item). */
        varianteFixe: (question, niveau) => Boolean(generateurDe(question, niveau)?.module.VARIANTES_FIXES),
        /** Le nouveau texte des variantes de l'item (compréhension de l'écrit), ou null. */
        texteDeLItem: (question, niveau) => generateurDe(question, niveau)?.module.SUPPORT || null,
        /**
         * Une variante de l'item (payload unifié), generatorKey = la clé de l'item ; null sans générateur.
         * format 'saisie' (l'interrupteur du QCM de suivi) : à saisir quand l'item le permet, en saisie ou en
         * texte à trous, sinon en QCM ; 'qcm' par défaut, comme l'évaluation de septembre.
         */
        genererVariante: (question, niveau, format = 'qcm') => {
            const g = generateurDe(question, niveau);
            if (!g) return null;
            const qcm = g.module.generate(g.item.formatKey);
            const aSaisir = format === 'saisie' ? formatDeSaisie(g.niveau, g.typeLabel, g.item.questionNum) : null;
            const payload = tableauCommeLOfficiel(aSaisir ? VERS_FORMAT[aSaisir](qcm) : qcm);
            return { ...payload, _meta: { ...payload._meta, generatorKey: generatorKeyItem(g.niveau, g.typeLabel, g.item.questionNum) } };
        },
    };
};

export const { aUnGenerateur, formatASaisir, varianteFixe, texteDeLItem, genererVariante } = creerRegistre(MODULES_EVALNAT);
